/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    Renderer2,
    SecurityContext,
    ViewChild
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {Subscription} from 'rxjs';
import {FileDataImageCacheService} from '../../service/file-data-image-cache.service';
import {TooltipImageComponent} from './tooltip-image.component';

@Component({
    selector: 'image-with-tooltip',
    template: `
        <!-- Main image that is always visible -->
        <div class="image-container">
          <img #mainImage alt="Image" class="component-image"
            (mouseover)="onMouseEnter($event)" (mouseleave)="onMouseLeave($event)">
            @if (!readOnly) {
              <button mat-icon-button (click)="handleRemoveClick()"
                class="remove-button" title="Remove Image">X
              </button>
            }
          </div>
          <!-- Zoomed image tooltip that appears on hover -->
          <app-tooltip-image/>
        `,
    styles: [`
         .component-image {
             max-height: 60px;
             max-width: 80px;
             object-fit: contain;
         }
        .remove-button {
            vertical-align: top;
            background-color: rgba(255, 0, 0, 0.2); /* Red background with some transparency */
            color: white;
            border: none;
            border-radius: 50%;
            width: 18px;
            height: 18px;
            align-items: center;
            cursor: pointer;
            font-size: 12px;
        }
        .remove-button:hover {
            background-color: rgba(255, 0, 0, 1); /* Fully opaque on hover */
        }
        .image-wrapper {
            display: inline-block;
            position: relative;
        }
        .tooltip {
            position: fixed;
            z-index: 1200;
            border: 5px solid black;
            border-radius: 5px;
            background: white;
        }
    `],
    standalone: false
})
export class ImageWithTooltipComponent implements AfterViewInit, OnDestroy {
    @ViewChild(TooltipImageComponent, { static: false }) tooltip!: TooltipImageComponent;
    @Input()
    set imageUrl(value: string) {
        this.rawImageUrl = value || '';
        this.resolveImageUrlForDisplay();
    }

    get imageUrl(): string {
        return this.rawImageUrl;
    }

    @Input() readOnly = false;
    @Output() onRemoveClick = new EventEmitter<void>();

    tooltipX = 0;
    tooltipY = 0;
    tooltipWidth = 0;
    private rawImageUrl = '';
    displayImageUrl = '';
    private boundFileDataId: number | null = null;
    private uncachedObjectUrl: string | null = null;
    private imageFetchSubscription?: Subscription;
    private destroyed = false;
    private pendingDisplayImageUrlTimeout: ReturnType<typeof setTimeout> | null = null;

    @ViewChild('mainImage', { static: false }) mainImage!: ElementRef<HTMLImageElement>;

    constructor(
        private renderer: Renderer2,
        public el: ElementRef,
        private fileDataImageCacheService: FileDataImageCacheService,
        private sanitizer: DomSanitizer
    ) {}

    ngAfterViewInit() {
        const parentElement = document.querySelector('.componentCatalog');
        if (parentElement && this.tooltip) {
            const tooltipElement = this.tooltip.el.nativeElement;
            this.renderer.appendChild(parentElement, tooltipElement);
        }

        this.applyImageSrcToElement();
        this.tooltipWidth = 0;
    }

    ngOnDestroy() {
        this.destroyed = true;
        if (this.pendingDisplayImageUrlTimeout !== null) {
            clearTimeout(this.pendingDisplayImageUrlTimeout);
            this.pendingDisplayImageUrlTimeout = null;
        }
        this.imageFetchSubscription?.unsubscribe();
        this.revokeUncachedObjectUrl();
        if (this.tooltip) {
            const tooltipElement = this.tooltip.el.nativeElement;
            tooltipElement.remove(); // Remove from DOM on destroy
        }
    }

    handleRemoveClick(): void {
        this.onRemoveClick.emit();
    }

    showTooltip() {
        const rect = this.mainImage.nativeElement.getBoundingClientRect();
        const x = rect.left + rect.width / 2; // Center horizontally
        const y = rect.bottom;
        this.tooltip?.show(this.displayImageUrl, x, y, this.mainImage.nativeElement);
    }

    hideTooltip(relatedTarget: EventTarget | null) {
        this.tooltip?.leaveOriginalImage(relatedTarget);
    }

    // Mouse enters the main image
    onMouseEnter(event: MouseEvent): void {
        // Get the main image position
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        // Position tooltip to the right with a little offset
        this.tooltipX = rect.right + 10;
        this.tooltipY = rect.top;
        this.showTooltip();
    }

    // Mouse leaves the main image; preserve the popup when it is the pointer's next target.
    onMouseLeave(event: MouseEvent): void {
        this.hideTooltip(event.relatedTarget);
    }

    private resolveImageUrlForDisplay(): void {
        const isHttp = this.rawImageUrl.toLowerCase().startsWith('http');
        const newFileDataId = isHttp ? FileDataImageCacheService.parseFileDataId(this.rawImageUrl) : null;

        if (newFileDataId != null && newFileDataId === this.boundFileDataId && this.displayImageUrl) {
            return;
        }

        this.imageFetchSubscription?.unsubscribe();

        if (!this.rawImageUrl) {
            this.revokeUncachedObjectUrl();
            this.boundFileDataId = null;
            this.setDisplayImageUrlDeferred('');
            return;
        }

        if (!isHttp) {
            this.revokeUncachedObjectUrl();
            this.boundFileDataId = null;
            this.setDisplayImageUrlDeferred(this.rawImageUrl);
            return;
        }

        this.revokeUncachedObjectUrl();
        this.boundFileDataId = newFileDataId;

        this.imageFetchSubscription = this.fileDataImageCacheService.getDisplayUrl(this.rawImageUrl).subscribe({
            next: (objectUrl: string) => {
                if (newFileDataId == null) {
                    this.uncachedObjectUrl = objectUrl;
                }
                this.setDisplayImageUrlDeferred(objectUrl);
            },
            error: () => {
                this.boundFileDataId = null;
                this.revokeUncachedObjectUrl();
                this.setDisplayImageUrlDeferred('');
            }
        });
    }

    private revokeUncachedObjectUrl(): void {
        if (!this.uncachedObjectUrl) {
            return;
        }
        URL.revokeObjectURL(this.uncachedObjectUrl);
        this.uncachedObjectUrl = null;
    }

    private setDisplayImageUrlDeferred(url: string): void {
        if (this.pendingDisplayImageUrlTimeout !== null) {
            clearTimeout(this.pendingDisplayImageUrlTimeout);
        }
        this.pendingDisplayImageUrlTimeout = setTimeout(() => {
            this.pendingDisplayImageUrlTimeout = null;
            if (!this.destroyed) {
                this.displayImageUrl = url;
                this.applyImageSrcToElement();
            }
        }, 0);
    }

    private applyImageSrcToElement(): void {
        if (!this.mainImage?.nativeElement) {
            return;
        }
        if (this.displayImageUrl) {
            const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, this.displayImageUrl);
            if (sanitizedUrl) {
                this.renderer.setAttribute(this.mainImage.nativeElement, 'src', sanitizedUrl);
                return;
            }
        }
        this.renderer.removeAttribute(this.mainImage.nativeElement, 'src');
    }

}
